import { useAuthStore } from "@/stores/authStore";
import type { ApiResponse, PaginatedResponse } from "@shared/types";

const API_URLS = [
  import.meta.env.VITE_API_URL,
  "/api",
  "https://orcamento.vintagelab.com.br/api",
  "https://orcamento-api.vintagelab.com.br/api",
].filter(Boolean) as string[];

let activeBaseUrl = API_URLS[0];

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function fetchWithFallback(endpoint: string, options: RequestInit) {
  let lastError: unknown;

  for (const baseUrl of API_URLS) {
    const url = `${baseUrl}${endpoint}`;

    try {
      const response = await fetch(url, options);

      if (response.ok || response.status < 500) {
        activeBaseUrl = baseUrl;
        return response;
      }

      lastError = new Error(`HTTP ${response.status}`);
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error("Falha ao conectar na API");
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {},
  token?: string,
): Promise<T> {
  const storedToken = token || useAuthStore.getState().token;

  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...options.headers,
  };

  if (storedToken) {
    (headers as Record<string, string>)["Authorization"] =
      `Bearer ${storedToken}`;
  }

  const response = await fetchWithFallback(endpoint, {
    ...options,
    headers,
  });

  const url = `${activeBaseUrl}${endpoint}`;
  console.log(`[API] ${options.method || "GET"} ${url}`);

  const data = await response.json();

  if (!response.ok) {
    console.error(
      `[API Error] ${response.status} ${response.statusText}`,
      data,
    );
    if (response.status === 401) {
      useAuthStore.getState().logout();
    }
    throw new ApiError(response.status, data.error || "Erro na requisição");
  }

  return data;
}

// Auth
export const authApi = {
  login: (email: string, password: string) =>
    request<ApiResponse<{ user: any; token: string; expires_at: string }>>(
      "/auth/login",
      {
        method: "POST",
        body: JSON.stringify({ email, password }),
      },
    ),

  logout: () => request<ApiResponse>("/auth/logout", { method: "POST" }),

  me: () => request<ApiResponse<any>>("/auth/me"),

  forgotPassword: (email: string) =>
    request<ApiResponse>("/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),

  resetPassword: (token: string, password: string) =>
    request<ApiResponse>("/auth/reset-password", {
      method: "POST",
      body: JSON.stringify({ token, password }),
    }),

  requestUnlock: (email: string) =>
    request<ApiResponse>("/auth/request-unlock", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),

  unlock: (email: string, code: string) =>
    request<ApiResponse>("/auth/unlock", {
      method: "POST",
      body: JSON.stringify({ email, code }),
    }),

  getCompany: (companyId: string) =>
    request<ApiResponse<any>>(`/companies/${companyId}`),

  getCompanyWithToken: (companyId: string, token: string) =>
    request<ApiResponse<any>>(`/companies/${companyId}`, {}, token),

  updateProfile: (data: any) =>
    request<ApiResponse<any>>("/auth/me", {
      method: "PUT",
      body: JSON.stringify(data),
    }),
};

// Companies
export const companiesApi = {
  list: () => request<ApiResponse<any[]>>("/companies"),

  get: (id: string) => request<ApiResponse<any>>(`/companies/${id}`),

  create: (data: any) =>
    request<ApiResponse<any>>("/companies", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  update: (id: string, data: any) =>
    request<ApiResponse>(`/companies/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    request<ApiResponse>(`/companies/${id}`, { method: "DELETE" }),

  addDomain: (
    companyId: string,
    data: { domain: string; is_primary?: boolean },
  ) =>
    request<ApiResponse<any>>(`/companies/${companyId}/domains`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  removeDomain: (companyId: string, domainId: string) =>
    request<ApiResponse>(`/companies/${companyId}/domains/${domainId}`, {
      method: "DELETE",
    }),
};

// Clients
export const clientsApi = {
  list: (params?: { search?: string; page?: number; per_page?: number }) =>
    request<ApiResponse<PaginatedResponse<any>>>(
      `/clients?${new URLSearchParams(params as any)}`,
    ),

  get: (id: string) => request<ApiResponse<any>>(`/clients/${id}`),

  create: (data: any) =>
    request<ApiResponse<any>>("/clients", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  update: (id: string, data: any) =>
    request<ApiResponse>(`/clients/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    request<ApiResponse>(`/clients/${id}`, { method: "DELETE" }),

  addContact: (clientId: string, data: any) =>
    request<ApiResponse<any>>(`/clients/${clientId}/contacts`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  removeContact: (clientId: string, contactId: string) =>
    request<ApiResponse>(`/clients/${clientId}/contacts/${contactId}`, {
      method: "DELETE",
    }),
};

// Products
export const productsApi = {
  listCategories: () => request<ApiResponse<any[]>>("/products/categories"),

  createCategory: (data: any) =>
    request<ApiResponse<any>>("/products/categories", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  updateCategory: (id: string, data: any) =>
    request<ApiResponse>(`/products/categories/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  deleteCategory: (id: string) =>
    request<ApiResponse>(`/products/categories/${id}`, { method: "DELETE" }),

  list: (params?: {
    category_id?: string;
    search?: string;
    page?: number;
    per_page?: number;
  }) => {
    const query = new URLSearchParams(
      Object.entries(params || {}).filter(
        ([, value]) =>
          value !== undefined &&
          value !== null &&
          value !== "" &&
          value !== "undefined",
      ) as Array<[string, string]>,
    );

    return request<ApiResponse<PaginatedResponse<any>>>(
      `/products?${query.toString()}`,
    );
  },

  get: (id: string) => request<ApiResponse<any>>(`/products/${id}`),

  create: (data: any) =>
    request<ApiResponse<any>>("/products", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  update: (id: string, data: any) =>
    request<ApiResponse>(`/products/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    request<ApiResponse>(`/products/${id}`, { method: "DELETE" }),

  updateOrder: (items: { id: string; sort_order: number }[]) =>
    request<ApiResponse>("/products/bulk-update-order", {
      method: "POST",
      body: JSON.stringify({ items }),
    }),
};

// Budgets
export const budgetsApi = {
  list: (params?: {
    status?: string;
    client_id?: string;
    search?: string;
    page?: number;
    per_page?: number;
    sort_by?: string;
    sort_order?: "asc" | "desc";
  }) => {
    const filteredParams = Object.fromEntries(
      Object.entries(params || {}).filter(
        ([, value]) =>
          value !== undefined &&
          value !== null &&
          value !== "" &&
          value !== "undefined",
      ),
    );
    return request<ApiResponse<PaginatedResponse<any>>>(
      `/budgets?${new URLSearchParams(filteredParams as any)}`,
    );
  },

  stats: () => request<ApiResponse<any>>("/budgets/stats"),

  get: (id: string) => request<ApiResponse<any>>(`/budgets/${id}`),

  create: (data: any) =>
    request<ApiResponse<{ id: string; number: string; total: number }>>(
      "/budgets",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    ),

  update: (id: string, data: any) =>
    request<ApiResponse>(`/budgets/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  send: (id: string) =>
    request<ApiResponse>(`/budgets/${id}/send`, { method: "POST" }),

  approve: (id: string) =>
    request<ApiResponse>(`/budgets/${id}/approve`, { method: "POST" }),

  reject: (id: string, reason?: string) =>
    request<ApiResponse>(`/budgets/${id}/reject`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  duplicate: (id: string) =>
    request<ApiResponse<{ id: string; number: string }>>(
      `/budgets/${id}/duplicate`,
      { method: "POST" },
    ),

  delete: (id: string) =>
    request<ApiResponse>(`/budgets/${id}`, { method: "DELETE" }),
};
// Users
export const usersApi = {
  list: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    role?: string;
  }) =>
    request<ApiResponse<PaginatedResponse<any>>>(
      `/users?${new URLSearchParams(params as any)}`,
    ),

  get: (id: string) => request<ApiResponse<any>>(`/users/${id}`),

  create: (data: any) =>
    request<ApiResponse<any>>("/users", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  update: (id: string, data: any) =>
    request<ApiResponse>(`/users/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    request<ApiResponse>(`/users/${id}`, { method: "DELETE" }),
};
// Public (para cliente ver orçamento)
export const publicApi = {
  getBudget: (id: string) => request<ApiResponse<any>>(`/public/budget/${id}`),

  validateDomain: (domain: string) =>
    request<ApiResponse<any>>(
      `/public/domain-check?${new URLSearchParams({ domain })}`,
    ),

  approveBudget: (
    id: string,
    data?: { notes?: string; selected_items?: string[] },
  ) =>
    request<ApiResponse>(`/public/budget/${id}/approve`, {
      method: "POST",
      body: JSON.stringify(data || {}),
    }),

  rejectBudget: (id: string, reason?: string) =>
    request<ApiResponse>(`/public/budget/${id}/reject`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  signBudget: (id: string, signature: string) =>
    request<ApiResponse>(`/public/budget/${id}/sign`, {
      method: "POST",
      body: JSON.stringify({ signature }),
    }),
};

// Notifications
export const notificationsApi = {
  list: (params?: {
    page?: number;
    per_page?: number;
    unread_only?: boolean;
  }) =>
    request<ApiResponse<any>>(
      `/notifications?${new URLSearchParams({
        page: String(params?.page || 1),
        per_page: String(params?.per_page || 10),
        unread_only: params?.unread_only ? "true" : "false",
      })}`,
    ),

  markRead: (id: string) =>
    request<ApiResponse>(`/notifications/${id}/read`, { method: "POST" }),

  markAllRead: () =>
    request<ApiResponse>(`/notifications/read-all`, { method: "POST" }),
};

// Client (portal do cliente)
export const clientApi = {
  listBudgets: (params?: {
    status?: string;
    search?: string;
    page?: number;
    per_page?: number;
  }) =>
    request<ApiResponse<PaginatedResponse<any>>>(
      `/client/budgets?${new URLSearchParams(params as any)}`,
    ),

  getBudget: (id: string) => request<ApiResponse<any>>(`/client/budgets/${id}`),

  approveBudget: (id: string, notes?: string) =>
    request<ApiResponse>(`/client/budgets/${id}/approve`, {
      method: "POST",
      body: JSON.stringify({ notes }),
    }),

  rejectBudget: (id: string, reason?: string) =>
    request<ApiResponse>(`/client/budgets/${id}/reject`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  requestChanges: (id: string, notes: string) =>
    request<ApiResponse>(`/client/budgets/${id}/request-changes`, {
      method: "POST",
      body: JSON.stringify({ notes }),
    }),
};

// Lookup (CEP e CNPJ)
export const lookupApi = {
  cep: (cep: string) =>
    request<
      ApiResponse<{
        cep: string;
        street: string;
        neighborhood: string;
        city: string;
        state: string;
      }>
    >(`/lookup/cep/${cep.replace(/\D/g, "")}`),

  cnpj: (cnpj: string) =>
    request<
      ApiResponse<{
        cnpj: string;
        razao_social: string;
        nome_fantasia: string;
        email: string;
        phone: string;
        address: {
          street: string;
          number: string;
          complement: string;
          neighborhood: string;
          city: string;
          state: string;
          cep: string;
        };
        partners: Array<{
          name: string;
          role: string;
        }>;
        situacao: string;
        porte: string;
        natureza_juridica: string;
        data_abertura: string;
        atividade_principal: string;
      }>
    >(`/lookup/cnpj/${cnpj.replace(/\D/g, "")}`),
};
